import React, { useEffect, useState } from "react";
import { api } from "./api";
import { useProblems, FieldProblem, invalid } from "./form-check";
import type { Rule } from "./form-check";

/**
 * The supplies catalog (build plan §10.1 #1): items by category, the OTC
 * products tied to the portal's OTC list, dosing (Supervisor / RN only), lots
 * with expiry, par and the session counts; the order list, what expires
 * before next year's session, and a printable count sheet. Works from a desk
 * weeks ahead. No stock epinephrine (D2 #23).
 */

export interface Lot {
  lot: string;
  expiresOn: string;
  qtyStart: string;
  qtyEnd: string;
  countedBy: string;
  countedAtUtc: string;
  removed: boolean;
  removedReason: string;
}
export interface SupplyItem {
  itemKey: string;
  name: string;
  category: string;
  otcListItemId: string;
  activeIngredient: string;
  product: string;
  strength: string;
  form: string;
  unit: string;
  parLevel: string;
  doseText: string;
  dosingIntervalHours: string;
  maxDosesPer24h: string;
  dosingSource: string;
  considerationPrompt: string;
  lots: Lot[];
  onHandStart: string;
  onHandEnd: string;
  active: boolean;
  updatedByEmail: string;
  updatedUtc: string;
  onHand: number;
}
interface View {
  items: SupplyItem[];
  orderList: { itemKey: string; name: string; category: string; unit: string; par: number; onHand: number; order: number }[];
  expiring: { itemKey: string; name: string; lot: string; expiresOn: string }[];
  nextSessionStartOn: string;
  otcCatalog: { id: string; name: string; use: string; topical: boolean }[];
  otcCatalogVersion: string;
  categories: string[];
  dosingSources: string[];
  canDosing: boolean;
}

export const CATEGORY_LABEL: Record<string, string> = { OTC: "OTC", FirstAid: "First aid", Consumable: "Consumable", Equipment: "Equipment" };
const SOURCE_LABEL: Record<string, string> = { Label: "Product label", StandingOrder: "Standing order" };
const TABS = [
  ["catalog", "Catalog"],
  ["order", "Order list"],
  ["expiring", "Expiring"],
  ["count", "Count sheet"],
] as const;

const blankItem = (): Omit<SupplyItem, "itemKey" | "updatedByEmail" | "updatedUtc" | "onHand"> => ({
  name: "",
  category: "OTC",
  otcListItemId: "",
  activeIngredient: "",
  product: "",
  strength: "",
  form: "",
  unit: "",
  parLevel: "",
  doseText: "",
  dosingIntervalHours: "",
  maxDosesPer24h: "",
  dosingSource: "",
  considerationPrompt: "",
  lots: [],
  onHandStart: "",
  onHandEnd: "",
  active: true,
});
type Draft = ReturnType<typeof blankItem>;

const whole = (v: string) => !v || /^\d{1,5}$/.test(v);
export function supplyRules(f: Draft): Rule[] {
  return [
    ["sp-name", !f.name.trim(), "Write the item's name."],
    ["sp-otc", f.category === "OTC" && !f.otcListItemId, "Choose which item on the OTC list this product is (the parent's permission is looked up by it)."],
    ["sp-par", !whole(f.parLevel), "Counts, par, hours and doses are whole numbers."],
    ["sp-start", !whole(f.onHandStart), "Counts, par, hours and doses are whole numbers."],
    ["sp-end", !whole(f.onHandEnd), "Counts, par, hours and doses are whole numbers."],
    ["sp-int", !whole(f.dosingIntervalHours), "Counts, par, hours and doses are whole numbers."],
    ["sp-max", !whole(f.maxDosesPer24h), "Counts, par, hours and doses are whole numbers."],
    ...f.lots.flatMap((l, i): Rule[] => [
      [`sp-lot-${i}`, !l.lot.trim(), "Each lot needs its lot number."],
      [`sp-exp-${i}`, !/^\d{4}-\d{2}-\d{2}$/.test(l.expiresOn), `Lot ${l.lot || i + 1}: give its expiry date.`],
      [`sp-why-${i}`, l.removed && !l.removedReason.trim(), `Lot ${l.lot || i + 1}: say why it was removed.`],
    ]),
  ];
}

function ItemForm({ v, initial, itemKey, onSaved, onCancel }: { v: View; initial: Draft; itemKey: string | null; onSaved: (v: View) => void; onCancel: () => void }) {
  const [f, setF] = useState<Draft>(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const set = (p: Partial<Draft>) => setF({ ...f, ...p });
  const setLot = (i: number, p: Partial<Lot>) => set({ lots: f.lots.map((l, j) => (j === i ? { ...l, ...p } : l)) });
  const { problems, check } = useProblems(supplyRules(f));
  const P = ({ k }: { k: string }) => <FieldProblem id={k} problems={problems} />;
  const otc = f.category === "OTC";
  const dosingLocked = !v.canDosing;
  async function save() {
    setError(null);
    if (!check()) return;
    setBusy(true);
    const r = await api<{ supplies: View }>(itemKey ? "PUT" : "POST", itemKey ? `/api/supplies/${encodeURIComponent(itemKey)}` : "/api/supplies", f);
    setBusy(false);
    if (!r.ok) return setError(r.data.error ?? "Not saved.");
    onSaved(r.data.supplies);
  }
  const field = (k: keyof Draft, id: string, label: string, extra: React.InputHTMLAttributes<HTMLInputElement> = {}) => (
    <div>
      <label htmlFor={id} className="field-label">{label}</label>
      <input id={id} className="field-input" {...invalid(id, problems)} {...extra} value={String(f[k] ?? "")} onChange={(e) => set({ [k]: e.target.value } as Partial<Draft>)} />
      <P k={id} />
    </div>
  );
  return (
    <div className="section-card border-program/40">
      <h3 className="text-base font-semibold">{itemKey ? `Edit ${initial.name}` : "Add an item"}</h3>
      <div className="mt-2 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {field("name", "sp-name", "Name")}
        <div>
          <label htmlFor="sp-cat" className="field-label">Category</label>
          <select id="sp-cat" className="field-input" value={f.category} onChange={(e) => set({ category: e.target.value, ...(e.target.value === "OTC" ? {} : { otcListItemId: "" }) })}>
            {v.categories.map((c) => <option key={c} value={c}>{CATEGORY_LABEL[c] ?? c}</option>)}
          </select>
        </div>
        {otc && (
          <div>
            <label htmlFor="sp-otc" className="field-label">OTC list item (permission lookup)</label>
            <select id="sp-otc" className="field-input" {...invalid("sp-otc", problems)} value={f.otcListItemId} onChange={(e) => set({ otcListItemId: e.target.value })}>
              <option value="">Choose…</option>
              {v.otcCatalog.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
            <P k="sp-otc" />
          </div>
        )}
        {field("activeIngredient", "sp-ai", "Active ingredient")}
        {field("product", "sp-prod", "Product (as stocked)")}
        {field("strength", "sp-str", "Strength")}
        {field("form", "sp-form", "Form")}
        {field("unit", "sp-unit", "Unit", { placeholder: "tablet, mL, each…" })}
        {field("parLevel", "sp-par", "Par level", { inputMode: "numeric" })}
        {field("onHandStart", "sp-start", "On hand at session start", { inputMode: "numeric" })}
        {field("onHandEnd", "sp-end", "On hand at session end", { inputMode: "numeric" })}
      </div>
      {otc && (
        <fieldset className="mt-3 rounded-md border border-slate-200 p-3" disabled={dosingLocked}>
          <legend className="px-1 text-sm font-semibold">Dosing {dosingLocked && <span className="font-normal text-slate-500">(RN or Supervisor)</span>}</legend>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {field("doseText", "sp-dose", "Dose", { placeholder: "1–2 tablets" })}
            {field("dosingIntervalHours", "sp-int", "At least (hours) between doses", { inputMode: "numeric" })}
            {field("maxDosesPer24h", "sp-max", "Most doses in 24 hours", { inputMode: "numeric" })}
            <div>
              <label htmlFor="sp-src" className="field-label">Dosing from</label>
              <select id="sp-src" className="field-input" value={f.dosingSource} onChange={(e) => set({ dosingSource: e.target.value })}>
                <option value="">—</option>
                {v.dosingSources.map((x) => <option key={x} value={x}>{SOURCE_LABEL[x] ?? x}</option>)}
              </select>
            </div>
          </div>
          <label htmlFor="sp-prompt" className="field-label mt-2">Consideration prompt (blank = none; the giver ticks it before recording a dose)</label>
          <textarea id="sp-prompt" className="field-input min-h-12" maxLength={500} value={f.considerationPrompt} onChange={(e) => set({ considerationPrompt: e.target.value })} />
        </fieldset>
      )}
      <fieldset className="mt-3">
        <legend className="text-sm font-semibold">Lots</legend>
        {f.lots.map((l, i) => (
          <div key={i} className="mt-2 grid gap-2 border-b border-slate-100 pb-2 sm:grid-cols-3 lg:grid-cols-6">
            <div><label htmlFor={`sp-lot-${i}`} className="field-label">Lot</label><input id={`sp-lot-${i}`} className="field-input" {...invalid(`sp-lot-${i}`, problems)} value={l.lot} onChange={(e) => setLot(i, { lot: e.target.value })} /><P k={`sp-lot-${i}`} /></div>
            <div><label htmlFor={`sp-exp-${i}`} className="field-label">Expires</label><input id={`sp-exp-${i}`} type="date" className="field-input" {...invalid(`sp-exp-${i}`, problems)} value={l.expiresOn} onChange={(e) => setLot(i, { expiresOn: e.target.value })} /><P k={`sp-exp-${i}`} /></div>
            <div><label htmlFor={`sp-qs-${i}`} className="field-label">Count, start</label><input id={`sp-qs-${i}`} className="field-input" inputMode="numeric" value={l.qtyStart} onChange={(e) => setLot(i, { qtyStart: e.target.value.replace(/\D/g, "") })} /></div>
            <div><label htmlFor={`sp-qe-${i}`} className="field-label">Count, end</label><input id={`sp-qe-${i}`} className="field-input" inputMode="numeric" value={l.qtyEnd} onChange={(e) => setLot(i, { qtyEnd: e.target.value.replace(/\D/g, "") })} /></div>
            <div className="flex flex-col justify-end text-sm">
              <label className="flex items-center gap-1.5"><input type="checkbox" checked={l.removed} onChange={(e) => setLot(i, { removed: e.target.checked })} />Removed</label>
              {l.countedBy && <span className="text-xs text-slate-500">counted by {l.countedBy}</span>}
            </div>
            {l.removed ? (
              <div><label htmlFor={`sp-why-${i}`} className="field-label">Why removed</label><input id={`sp-why-${i}`} className="field-input" {...invalid(`sp-why-${i}`, problems)} value={l.removedReason} onChange={(e) => setLot(i, { removedReason: e.target.value })} /><P k={`sp-why-${i}`} /></div>
            ) : (
              <div className="flex items-end"><button type="button" className="text-xs text-program underline" onClick={() => set({ lots: f.lots.filter((_x, j) => j !== i) })}>Take this lot off (typed in error)</button></div>
            )}
          </div>
        ))}
        <button type="button" className="btn-secondary mt-2" onClick={() => set({ lots: [...f.lots, { lot: "", expiresOn: "", qtyStart: "", qtyEnd: "", countedBy: "", countedAtUtc: "", removed: false, removedReason: "" }] })}>Add a lot</button>
      </fieldset>
      <label className="mt-3 flex items-center gap-2 text-sm"><input type="checkbox" checked={f.active} onChange={(e) => set({ active: e.target.checked })} />Active (stocked this session)</label>
      {error && <p className="field-error" role="alert">{error}</p>}
      <div className="mt-3 flex flex-wrap gap-2">
        <button type="button" className="btn-primary" disabled={busy} onClick={() => void save()}>Save</button>
        <button type="button" className="btn-secondary" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

function CountSheet({ v }: { v: View }) {
  return (
    <div className="section-card">
      <div className="flex flex-wrap items-baseline justify-between gap-2 print:hidden">
        <p className="text-sm text-slate-600">One line per lot. Print it, count, then enter the counts on each item.</p>
        <button type="button" className="btn-secondary" onClick={() => window.print()}>Print</button>
      </div>
      <h3 className="mt-2 text-base font-semibold">Supplies count sheet</h3>
      <table className="mt-2 w-full border-collapse text-sm">
        <thead>
          <tr>{["Item", "Unit", "Par", "Lot", "Expires", "Count", "Initials"].map((h) => <th key={h} className="border border-slate-400 px-1 py-1 text-left">{h}</th>)}</tr>
        </thead>
        <tbody>
          {v.items.filter((i) => i.active).flatMap((i) => {
            const lots = i.lots.filter((l) => !l.removed);
            return (lots.length ? lots : [null]).map((l, j) => (
              <tr key={`${i.itemKey}-${j}`}>
                <td className="border border-slate-400 px-1 py-2">{j === 0 ? `${i.name}${i.strength ? ` ${i.strength}` : ""} (${CATEGORY_LABEL[i.category] ?? i.category})` : ""}</td>
                <td className="border border-slate-400 px-1 py-2">{i.unit}</td>
                <td className="border border-slate-400 px-1 py-2">{j === 0 ? i.parLevel : ""}</td>
                <td className="border border-slate-400 px-1 py-2">{l?.lot ?? ""}</td>
                <td className="border border-slate-400 px-1 py-2">{l?.expiresOn ?? ""}</td>
                <td className="w-24 border border-slate-400 px-1 py-2" />
                <td className="w-20 border border-slate-400 px-1 py-2" />
              </tr>
            ));
          })}
        </tbody>
      </table>
    </div>
  );
}

export function Supplies(): React.ReactElement {
  const [v, setV] = useState<View | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<(typeof TABS)[number][0]>("catalog");
  const [editing, setEditing] = useState<SupplyItem | "new" | null>(null);
  useEffect(() => {
    void api<{ supplies: View }>("GET", "/api/supplies").then((r) => (r.ok ? setV(r.data.supplies) : setError(r.data.error ?? "Could not load the supplies.")));
  }, []);
  if (error) return <div className="section-card" role="alert"><p className="text-sm text-red-700">{error}</p></div>;
  if (!v) return <p className="text-sm text-slate-600">Loading…</p>;
  const saved = (nv: View) => {
    setV(nv);
    setEditing(null);
  };
  const otcName = (id: string) => v.otcCatalog.find((o) => o.id === id)?.name ?? id;
  return (
    <section>
      <h2 className="mb-1 text-xl font-semibold print:hidden">Supplies</h2>
      <p className="mb-3 text-sm text-slate-600 print:hidden">The OTC products, first aid, consumables and equipment as stocked. An OTC product is tied to the item on the families' OTC list, so the parent's permission is looked up by it. No stock epinephrine: students bring their own. OTC list {v.otcCatalogVersion}.</p>
      <div className="mb-3 flex flex-wrap gap-2 print:hidden" role="tablist">
        {TABS.map(([k, label]) => (
          <button key={k} type="button" role="tab" aria-selected={tab === k} className={`rounded-md border px-3 py-1.5 text-sm ${tab === k ? "border-program bg-program text-white" : "border-slate-300 bg-white"}`} onClick={() => setTab(k)}>
            {label}
            {k === "order" && v.orderList.length > 0 && ` (${v.orderList.length})`}
            {k === "expiring" && v.expiring.length > 0 && ` (${v.expiring.length})`}
          </button>
        ))}
      </div>
      {tab === "catalog" && (
        <>
          {editing ? (
            <ItemForm key={editing === "new" ? "new" : editing.itemKey} v={v} initial={editing === "new" ? blankItem() : editing} itemKey={editing === "new" ? null : editing.itemKey} onSaved={saved} onCancel={() => setEditing(null)} />
          ) : (
            <button type="button" className="btn-primary mb-3" onClick={() => setEditing("new")}>Add an item</button>
          )}
          {v.categories.map((c) => {
            const items = v.items.filter((i) => i.category === c);
            if (!items.length) return null;
            return (
              <div key={c} className="section-card overflow-x-auto">
                <h3 className="mb-2 text-base font-semibold">{CATEGORY_LABEL[c] ?? c} ({items.length})</h3>
                <table className="w-full min-w-[40rem] text-sm">
                  <thead className="text-left text-slate-600">
                    <tr><th className="py-1">Item</th>{c === "OTC" && <th>Dose · interval · max/24 h</th>}<th>Par</th><th>On hand</th><th>Lots</th><th /></tr>
                  </thead>
                  <tbody>
                    {items.map((i) => (
                      <tr key={i.itemKey} className={`border-t border-slate-100 ${i.active ? "" : "text-slate-400"}`}>
                        <td className="py-1.5">
                          <span className="font-medium">{i.name}</span>{i.strength ? ` ${i.strength}` : ""}
                          {c === "OTC" && <span className="block text-xs text-slate-500">{otcName(i.otcListItemId)}{i.considerationPrompt ? " · consideration prompt" : ""}</span>}
                          {!i.active && <span className="block text-xs">Not stocked</span>}
                        </td>
                        {c === "OTC" && <td>{[i.doseText, i.dosingIntervalHours && `every ${i.dosingIntervalHours} h`, i.maxDosesPer24h && `max ${i.maxDosesPer24h}`].filter(Boolean).join(" · ") || "—"}{i.dosingSource && <span className="block text-xs text-slate-500">{SOURCE_LABEL[i.dosingSource]}</span>}</td>}
                        <td>{i.parLevel || "—"}</td>
                        <td className={i.parLevel && i.onHand < Number(i.parLevel) ? "font-semibold text-amber-900" : ""}>{i.onHand}</td>
                        <td>{i.lots.filter((l) => !l.removed).length}</td>
                        <td className="text-right"><button type="button" className="text-program underline" onClick={() => setEditing(i)}>Edit</button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })}
        </>
      )}
      {tab === "order" && (
        <div className="section-card">
          <h3 className="mb-2 text-base font-semibold">Order from logistics (par − on hand)</h3>
          {v.orderList.length === 0 ? <p className="text-sm text-slate-600">Nothing is below par.</p> : (
            <table className="w-full text-sm">
              <thead className="text-left text-slate-600"><tr><th className="py-1">Item</th><th>Par</th><th>On hand</th><th>Order</th></tr></thead>
              <tbody>{v.orderList.map((x) => <tr key={x.itemKey} className="border-t border-slate-100"><td className="py-1.5">{x.name} <span className="text-xs text-slate-500">({CATEGORY_LABEL[x.category] ?? x.category})</span></td><td>{x.par}</td><td>{x.onHand}</td><td className="font-semibold">{x.order} {x.unit}</td></tr>)}</tbody>
            </table>
          )}
          <button type="button" className="btn-secondary mt-3 print:hidden" onClick={() => window.print()}>Print</button>
        </div>
      )}
      {tab === "expiring" && (
        <div className="section-card">
          <h3 className="mb-2 text-base font-semibold">Expiring before next year's session{v.nextSessionStartOn ? ` (${v.nextSessionStartOn})` : ""}</h3>
          {!v.nextSessionStartOn ? <p className="text-sm text-amber-900">Next year's first day is not set (Config: Session dates).</p> : v.expiring.length === 0 ? <p className="text-sm text-slate-600">No lot expires before then.</p> : (
            <ul className="text-sm">{v.expiring.map((x) => <li key={`${x.itemKey}-${x.lot}`}>{x.name}: lot {x.lot}, expires {x.expiresOn}</li>)}</ul>
          )}
        </div>
      )}
      {tab === "count" && <CountSheet v={v} />}
    </section>
  );
}
